import { isUndefined } from 'lodash-es';
import { type ReactNode } from 'react';

import { defaultRatio, ORIENTATION } from '../../../constants.js';
import {
  ChartLayoutDimensions,
  ChartLayoutSettings,
} from '../types/chart-layout.js';
import {
  DEFAULT_LEGEND_CONFIG,
  DEFAULT_RATIO_BOUNDARIES,
} from '../types/constants.js';
import { extractLayoutSlots } from './extract-layout-slots.js';
import { getOrientation } from './orientation.js';
import { sanitizeLegendRatio } from './sanitize-legend-ratio.js';

/**
 * Function responsible to calculate the setup params for the chart layout (
 * e.g. orientation, resizable state, ratio etc.)
 * @param containerSizes -
 * @param children -
 * @param isEmpty -
 */
export const getChartLayoutSettings = (
  containerSizes: ChartLayoutDimensions,
  children: ReactNode,
  isEmpty: boolean,
): ChartLayoutSettings => {
  const { provider, graphArea, legendArea, legendProps } =
    extractLayoutSlots(children);

  const containerAvailableArea = {
    width: containerSizes.width,
    height: containerSizes.height,
  };

  if (!legendArea) {
    return {
      graphArea,
      containerAvailableArea,
      legendSettings: {
        orientation: ORIENTATION.Vertical,
        showResizer: false,
        hidden: true,
        ratio: 0,
        ratioBoundaries: DEFAULT_RATIO_BOUNDARIES,
      },
      provider,
    };
  }
  const {
    position,
    // oxlint-disable-next-line typescript/no-deprecated
    ratio = defaultRatio,
    initialRatio,
    onResize,
    overrideRatio,
    ratioBoundaries = DEFAULT_RATIO_BOUNDARIES,
  } = legendProps ?? DEFAULT_LEGEND_CONFIG;

  const orientation = getOrientation(
    containerAvailableArea.width,
    containerAvailableArea.height,
    position,
  );

  return {
    graphArea,
    legendArea,
    containerAvailableArea,
    legendSettings: {
      orientation,
      showResizer: !isEmpty,
      hidden: isUndefined(legendArea),
      ratio: sanitizeLegendRatio(ratio, ratioBoundaries, orientation),
      initialRatio: initialRatio
        ? sanitizeLegendRatio(initialRatio, ratioBoundaries, orientation)
        : undefined,
      overrideRatio,
      ratioBoundaries,
      onResize,
    },
    provider,
  };
};
